import React, { useState } from 'react';
import Button from '../atoms/Button';
import Icon from '../atoms/Icon';

const DocumentSelectionModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  candidateName,
  loading = false 
}) => {
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  // Available document types
  const availableDocuments = [
    { id: 'degree_certificate', label: 'Degree Certificate', description: 'University/College degree certificate' },
    { id: '10th_certificate', label: '10th Certificate', description: 'Secondary school certificate' },
    { id: '12th_certificate', label: '12th Certificate', description: 'Higher secondary school certificate' },
    { id: 'experience_letter', label: 'Experience Letter', description: 'Work experience letter from previous employer' },
    { id: 'relieving_letter', label: 'Relieving Letter', description: 'Relieving letter from previous employer' },
    { id: 'offer_letter', label: 'Offer Letter', description: 'Job offer letter from previous employer' },
    { id: 'pay_slip', label: 'Pay Slip', description: 'Salary slip from previous employer' },
    { id: 'bank_account', label: 'Bank Account Statement', description: 'Bank account statement' },
    { id: 'pan_card', label: 'PAN Card', description: 'Permanent Account Number card' },
    { id: 'aadhar_card', label: 'Aadhar Card', description: 'Aadhar identification card' },
    { id: 'passport', label: 'Passport', description: 'Passport for identity verification' },
    { id: 'driving_license', label: 'Driving License', description: 'Driving license for identity verification' },
    { id: 'other_certificate', label: 'Other Certificate', description: 'Any other relevant certificate' }
  ];

  const handleDocumentToggle = (documentId) => {
    setSelectedDocuments(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedDocuments.length === availableDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(availableDocuments.map(doc => doc.id));
    }
  };

  const handleConfirm = () => {
    if (selectedDocuments.length === 0) {
      alert('Please select at least one document type');
      return;
    }
    
    // Convert IDs to labels for email
    const selectedLabels = availableDocuments
      .filter(doc => selectedDocuments.includes(doc.id))
      .map(doc => doc.label);
    
    onConfirm(selectedLabels);
  };

  const handleClose = () => {
    setSelectedDocuments([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Request Document Verification
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Select documents to request from <strong>{candidateName}</strong>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <Icon name="close" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Select All Toggle */}
          <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">
              {selectedDocuments.length} of {availableDocuments.length} documents selected
            </span>
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              disabled={loading}
            >
              {selectedDocuments.length === availableDocuments.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Document List */}
          <div className="space-y-3">
            {availableDocuments.map((document) => (
              <div
                key={document.id}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedDocuments.includes(document.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !loading && handleDocumentToggle(document.id)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedDocuments.includes(document.id)
                        ? 'bg-blue-500 border-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedDocuments.includes(document.id) && (
                        <Icon name="check" size={14} className="text-white" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">
                      {document.label}
                    </h3>
                    <p className="text-xs text-gray-600 mt-1">
                      {document.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Note for HR */}
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="info" size={16} className="text-yellow-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Note for HR:</p>
                <p className="mt-1">
                  Candidates can upload any documents they have available. 
                  Fresh graduates may not have experience letters or pay slips. 
                  The system will allow flexible document submission.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={loading || selectedDocuments.length === 0}
            className="min-w-[120px]"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Sending...</span>
              </div>
            ) : (
              `Send Request (${selectedDocuments.length})`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentSelectionModal;
